import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../lib/api';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact`);
      setContacts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Помилка завантаження заявок');
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Ви впевнені, що хочете видалити цю заявку?')) {
      return;
    }

    try {
      setDeleting(contactId);
      await axios.delete(`${API}/contact/${contactId}`);
      
      // Remove from local state
      setContacts(contacts.filter(contact => contact.id !== contactId));
      
      // Show success message
      alert('Заявку успішно видалено!');
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Помилка видалення заявки. Спробуйте ще раз.');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceName = (service) => {
    const services = {
      'website': 'Розробка сайтів',
      'mobile': 'Мобільні додатки',
      'ecommerce': 'Інтернет-магазини',
      'design': 'UI/UX дизайн',
      'support': 'Технічна підтримка',
      'other': 'Інше'
    };
    return services[service] || service;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-blue-950 to-violet-950 flex items-center justify-center">
        <motion.div
          className="text-white text-xl"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Завантаження заявок...
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-blue-950 to-violet-950 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold text-white mb-4">
            Контактні заявки HOKS
          </h1>
          <p className="text-slate-300">
            Всього заявок: <span className="text-blue-400 font-bold">{contacts.length}</span>
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={fetchContacts}
            disabled={loading}
            className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 disabled:opacity-50 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg"
          >
            🔄 Оновити заявки
          </button>
        </motion.div>

        {/* Error State */}
        {error && (
          <motion.div
            className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 mb-8 text-red-300 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {error}
          </motion.div>
        )}

        {/* No Contacts */}
        {contacts.length === 0 && !loading && !error && (
          <motion.div
            className="text-center text-slate-400 text-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Поки що немає заявок 📭
          </motion.div>
        )}

        {/* Contacts List */}
        <div className="grid gap-6">
          {contacts.map((contact, index) => (
            <motion.div
              key={contact.id || index}
              className="bg-slate-800/60 backdrop-blur-lg rounded-lg p-6 border border-slate-700 shadow-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ y: -2 }}
            >
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2 flex items-center">
                      👤 {contact.name}
                      <span className="ml-3 text-sm bg-gradient-to-r from-blue-500 to-violet-500 text-white px-3 py-1 rounded-full">
                        {getServiceName(contact.service)}
                      </span>
                    </h3>
                  </div>

                  <div className="space-y-2">
                    <p className="text-slate-300 flex items-center">
                      📧 <span className="ml-2 text-blue-300">{contact.email}</span>
                    </p>
                    
                    {contact.phone && (
                      <p className="text-slate-300 flex items-center">
                        📞 <span className="ml-2 text-green-300">{contact.phone}</span>
                      </p>
                    )}
                    
                    <p className="text-slate-400 flex items-center text-sm">
                      ⏰ <span className="ml-2">{formatDate(contact.timestamp)}</span>
                    </p>
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <h4 className="text-slate-300 font-medium mb-2">💬 Повідомлення:</h4>
                  <div className="bg-slate-700/50 p-4 rounded-lg">
                    <p className="text-slate-200 leading-relaxed">
                      {contact.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Indicator and Delete Button */}
              <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-900/30 text-green-300">
                  ✅ Нова заявка
                </span>
                
                <motion.button
                  onClick={() => deleteContact(contact.id)}
                  disabled={deleting === contact.id}
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-400 hover:to-pink-400 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-md flex items-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {deleting === contact.id ? (
                    <>
                      <motion.div
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      <span>Видалення...</span>
                    </>
                  ) : (
                    <>
                      <span>🗑️</span>
                      <span>Видалити</span>
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Back to Site */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <a
            href="/"
            className="inline-flex items-center text-blue-300 hover:text-blue-200 transition-colors"
          >
            ← Повернутися на сайт
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminContacts;